import { EventEmitter, ViewChild } from '@angular/core';
import { Output } from '@angular/core';
import { ProjectColumnDefinitions, ProjectTableGroupHeader } from './../../project.model';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { ChangeProjectPartDialogComponent } from './../change-project-part-dialog/change-project-part-dialog.component';
import { Component, Input, OnInit, ViewChildren } from '@angular/core';
import { ConfigurationService, DialogService, NumberFormatService } from '@sdbn/appclient';
import { HttpService } from '@sdbn/appclient';
import { environment } from 'src/environments/environment';
import { Project, ProjectPart, ProjectPosition } from '../../project.model';
import { AddProjectPartDialogComponent } from '../add-project-part-dialog/add-project-part-dialog.component';
import { ProjectPositionEditorDialogComponent } from '../project-position-editor-dialog/project-position-editor-dialog.component';
import { ArticleTypeService } from 'src/erp-articles/article-types/article-type.service';
import { AttributeValueResolverService } from 'src/erp-articles/catalog/pipe/attribute-value.service';
import { TranslationService } from '@sdbn/common';
import { ArticleSelectionComponent } from 'src/erp-articles/erp-common/components/article-selection/article-selection.component';
import { DateTime } from 'luxon';
import { MatSort, Sort } from '@angular/material/sort';

const CACHE_PROJECT_TABLE_SORT_KEY = 'erp.project.table.sort';

@Component({
  selector: 'app-project-structure',
  templateUrl: './project-structure.component.html',
  styleUrls: ['./project-structure.component.scss']
})
export class ProjectStructureComponent implements OnInit {

  twoDigitsPriceFormat = '1.2-2';
  threeDigitsPriceFormat = '1.3-3';
  priceFormatDefinition = this.twoDigitsPriceFormat;
  useThreeDigits = false;
  priceScale = 2;

  tableAlignments = new Array<String>();
  tableHeaderAlignments = new Array<String>();

  sortDirection: any;
  sortColumn: string;

  @ViewChild('tableSort') tableSort = new MatSort();


  constructor(
    public cps: ConfigurationService,
    private dialogService: DialogService,
    private httpService: HttpService,
    private articleTypeService: ArticleTypeService,
    private numberFormatService: NumberFormatService,
    private attributeValueConverter: AttributeValueResolverService,
    public translationService: TranslationService
  ) {
    const tableHeaderDefinition = cps.getClientParamValue('erp.column-definition.projectpositions', '');
    const tableAlignments = cps.getClientParamValue('erp.column-alignments.projectpositions', '');
    const tableHeaderAlignments = cps.getClientParamValue('erp.header-alignments.projectpositions', '');
    console.log('header alignments', tableHeaderAlignments);
    this.tableAlignments.push(...tableAlignments.split(','));
    this.tableHeaderAlignments.push(...tableHeaderAlignments.split(','));
    console.log('header alignments', tableHeaderAlignments);
    this.displayedColumns.push(...tableHeaderDefinition.split(','));
    this.displayedColumns.push('positionActions');
    const useThreeDigits = this.cps.getClientParamValue('erp.use-three-digit-prices', 'false') === 'true';
    if (useThreeDigits) {
      this.priceFormatDefinition = this.threeDigitsPriceFormat;
      this.priceScale = 3;
    }
  }

  project: Project;
  columnDefinitions: ProjectColumnDefinitions;

  displayedColumns: string[] = [];
  datasource = new MatTableDataSource<any>();

  @Input('project') set setProject(value: Project) {
    this.project = value;
    this.buildTableModel();
  }

  @Output() projectChanged = new EventEmitter<void>();


  ngOnInit(): void {

    this.httpService
      .postWithContext<ProjectColumnDefinitions>(environment.erpProjectEndpoint + '/getColumnDefinitions', {})
      .subscribe(value => {
        this.columnDefinitions = new ProjectColumnDefinitions(value);
        this.buildTableModel();
      });
    this.restoreTableSort();
  }

  isHeader(index, item): boolean {
    return item instanceof ProjectTableGroupHeader;
  }

  retrieveValueForColumn(index: number, projectPosition: ProjectPosition) {
    if (this.columnDefinitions == null) {
      return '';
    }

    const column = this.columnDefinitions.definitions.get('' + projectPosition.articleTypeId)[index];
    if (column != null && column.indexOf('.') > 0) {
      const v = column.split('.');
      if (v[0] === 'project') {
        if (v[1] === 'id') {
          return this.project.id;
        } else if (v[1] === 'name') {
          return this.project.name;
        }
      } else if (v[0] === 'projectposition') {
        if (v[1] === 'identifier') {
          return projectPosition.articleIdentifier;
        } else if (v[1] === 'amount') {
          return projectPosition.amount != null ? this.numberFormatService.format(projectPosition.amount, 3) : '';
        } else if (v[1] === 'description') {
          return projectPosition.description;
        } else if (v[1] === 'name') {
          return projectPosition.name;
        } else if (v[1] === 'sumNet') {
          return projectPosition.sumNet != null ? this.numberFormatService.format(projectPosition.sumNet, this.priceScale) : '';
        } else if (v[1] === 'referenceDate') {
          return projectPosition.referenceDate != null ? DateTime.fromJSDate(projectPosition.referenceDate, {}).toFormat('dd.MM.yy') : '';
        }
      } else if (v[0] === 'attribute') {
        const articleType = this.articleTypeService.getById(projectPosition.articleTypeId).completeArticleType;
        const value = projectPosition.valuesMap.get(v[1]);
        return this.attributeValueConverter.transform(value, articleType.findArticleTypeAttributeById(Number.parseInt(v[1])));
      }
    }
    return '';
  }

  retrieveSortValueForColumn(index: number, projectPosition: ProjectPosition) {
    if (this.columnDefinitions == null) {
      return '';
    }
    const column = this.columnDefinitions.definitions.get('' + projectPosition.articleTypeId)[index];
    if (column != null && column.indexOf('.') > 0) {
      const v = column.split('.');
      if (v[0] === 'project') {
        if (v[1] === 'id') {
          return this.project.id;
        } else if (v[1] === 'name') {
          return this.project.name;
        }
      } else if (v[0] === 'projectposition') {
        if (v[1] === 'identifier') {
          return projectPosition.articleIdentifier;
        } else if (v[1] === 'amount') {
          return projectPosition.amount != null ? this.numberFormatService.addLeadingZeroes(projectPosition.amount, 12) : '';
        } else if (v[1] === 'description') {
          return projectPosition.description;
        } else if (v[1] === 'name') {
          return projectPosition.name;
        } else if (v[1] === 'sumNet') {
          // 1. Nachkommastellen auf 3 Stellen normalisiert werden
          const sumNet = projectPosition.sumNet != null ? this.numberFormatService.format(projectPosition.sumNet, this.priceScale) : '';
          // 2. String durch Vroanstellen von 0 auf einheitliche Länge von 12 bringen
          return this.numberFormatService.addLeadingZeroes(sumNet, 12);
        } else if (v[1] === 'referenceDate') {
          return projectPosition.referenceDate != null ? DateTime.fromJSDate(projectPosition.referenceDate, {}).toFormat('yyyMMdd') : '';
        }
      } else if (v[0] === 'attribute') {
        const articleType = this.articleTypeService.getById(projectPosition.articleTypeId).completeArticleType;
        const value = projectPosition.valuesMap.get(v[1]);
        return this.attributeValueConverter.transform(value, articleType.findArticleTypeAttributeById(Number.parseInt(v[1])));
      }
    }
    return '';
  }



  private buildTableModel() {
    let level = 0;
    if (this.project != null && this.project.parts != null) {
      const data = [];
      this.mapCataloNodesToTableModel(data, level, this.project.parts);
      this.datasource = new MatTableDataSource(data);
      this.datasource.sortingDataAccessor = (item, property) => {
        if (item instanceof ProjectPosition) {
          const index = this.displayedColumns.indexOf(property);
          if (index >= 0) {
            return item.projectPartId + '_' + (this.tableSort.direction == 'desc' ? '0' : '1') + '_' + this.retrieveSortValueForColumn(index, item);
          }
        }

        if (item instanceof ProjectTableGroupHeader) {
          return item.projectPart.id + '_' + (this.tableSort.direction == 'desc' ? '1' : '0');
        }

        return '';
      };
      this.datasource.sort = this.tableSort;
    }
  }

  private mapCataloNodesToTableModel(data: Array<any>, level: number, nodes: Array<ProjectPart>) {
    nodes.forEach(projectPart => {
      data.push(new ProjectTableGroupHeader({ level: level, name: projectPart.name, projectPart: projectPart }));
      if (projectPart.positions != null && projectPart.positions.length > 0) {
        projectPart.positions.forEach(entry => {
          data.push(entry);
        });
      }
      if (projectPart.parts != null) {
        this.mapCataloNodesToTableModel(data, level + 1, projectPart.parts);
      }
    });
  }

  addPosition(node: ProjectPart) {

    this.dialogService.openDefault(ArticleSelectionComponent, {})
      .afterClosed().subscribe(selectedArticle => {

        this.dialogService.openLarge(ProjectPositionEditorDialogComponent, { selectedArticleId: selectedArticle.id, parentPartId: node.id })
          .afterClosed().subscribe(value => {
            this.projectChanged.next();
          });
      });

  }

  editPosition(position: ProjectPosition) {
    this.dialogService.openLarge(ProjectPositionEditorDialogComponent, { position: position })
      .afterClosed().subscribe(value => {
        this.projectChanged.next();
      });
  }

  deletePosition(position: ProjectPosition) {
    this.dialogService.showConfirm('erp.ui.project-admin.dialog.remove-projectposition.title', 'erp.ui.project-admin.dialog.remove-projectposition.message')
      .afterClosed().subscribe(confirmed => {
        if (confirmed) {
          this.httpService.postWithContext<number>(environment.erpProjectEndpoint + '/deleteProjectPosition', { projectPositionId: position.id })
            .subscribe(response => {
              this.projectChanged.next();
            }, error => {
              console.log('error is', error);
            });
        }
      });
  }

  addPart(part: ProjectPart) {
    this.dialogService.openDefault(AddProjectPartDialogComponent, { projectId: this.project.id, parentPartId: part.id })
      .afterClosed().subscribe(() => {
        this.projectChanged.next();
      });
  }

  editPart(part: ProjectPart) {
    this.dialogService.openDefault(ChangeProjectPartDialogComponent, { projectId: this.project.id, projectPartId: part.id, name: part.name })
      .afterClosed().subscribe(() => {
        this.projectChanged.next();
      });
  }

  deletePart(part: ProjectPart) {

    this.dialogService.showConfirm('erp.ui.project-admin.dialog.remove-projectpart.title', 'erp.ui.project-admin.dialog.remove-projectpart.message')
      .afterClosed().subscribe(confirmed => {
        if (confirmed) {
          this.httpService.postWithContext<number>(environment.erpProjectEndpoint + '/deleteProjectPart', { partId: part.id })
            .subscribe(response => {
              this.projectChanged.next();
            }, error => {
              console.log('error is', error);
            });
        }
      });
  }

  public sortChanged(event) {
    this.storeTableSort(event.active, event.direction);
  }

  private restoreTableSort() {
    const tableSort = this.getFromLocalStorage(CACHE_PROJECT_TABLE_SORT_KEY);
    console.log('sorting is', tableSort);
    if (tableSort != null) {
      // this.sortColumn = tableSort.sortColumn;
      // this.sortDirection = tableSort.sortDirection;
      this.sortDirection = tableSort.sortDirection;
      this.sortColumn = tableSort.sortColumn;
    }
  }

  private storeTableSort(sortColumn: string, sortDirection: string) {
    this.saveToLocalStorage(CACHE_PROJECT_TABLE_SORT_KEY,
      { sortColumn: sortColumn, sortDirection: sortDirection });
  }

  saveToLocalStorage(key: string, value: any) {
    localStorage.setItem(key, JSON.stringify(value));
  }

  getFromLocalStorage(key: string): any {
    const val = localStorage.getItem(key);
    if (val) {
      try {
        return JSON.parse(val);
      } catch (e) {
        console.log('Could not parse as Json. Key: [' + key + '], value as string: [' + val + ']');
      }
    }
    return null;
  }

}
